import { Injectable } from '@angular/core';
import { ERROR_CODES } from '../constants/fhir.constants';
import type { ConversionError } from '../interfaces/fhir-converter.interfaces';

/** Valid FHIR R4 EncounterLocationStatus values. */
const VALID_LOCATION_STATUSES = ['planned', 'active', 'reserved', 'completed'] as const;

/**
 * FHIR R4 Encounter Validator
 *
 * Implements FHIR R4 Encounter validation for sub-element required fields:
 *
 * - `statusHistory`: `status` (1..1), `period` (1..1)
 * - `classHistory`: `class` (1..1), `period` (1..1)
 * - `diagnosis`: `condition` (1..1)
 * - `location`: `location` (1..1), `status` value-set (`planned | active | reserved | completed`)
 */
@Injectable({ providedIn: 'root' })
export class FhirEncounterValidator {
  validate(resource: unknown, errors: ConversionError[] = [], warnings: string[] = []): boolean {
    const r = resource as Record<string, unknown>;

    this.validateStatusHistory(r, errors);
    this.validateClassHistory(r, errors);
    this.validateDiagnoses(r, errors);
    this.validateLocations(r, errors);

    // Informational warnings
    if (!r['subject']) {
      warnings.push('Encounter does not reference a subject (patient)');
    }

    return !errors.some((e) => e.severity === 'error');
  }

  /** StatusHistory: status (1..1), period (1..1). */
  private validateStatusHistory(r: Record<string, unknown>, errors: ConversionError[]): void {
    const history = r['statusHistory'] as Record<string, unknown>[] | undefined;
    if (!history || !Array.isArray(history)) return;

    for (let i = 0; i < history.length; i++) {
      if (!history[i]['status']) {
        errors.push({
          code: ERROR_CODES.ENC_STATUSHISTORY_MISSING_STATUS,
          message: `statusHistory[${i}].status is required`,
          path: `statusHistory[${i}].status`,
          severity: 'error',
        });
      }
      if (!history[i]['period']) {
        errors.push({
          code: ERROR_CODES.ENC_STATUSHISTORY_MISSING_PERIOD,
          message: `statusHistory[${i}].period is required`,
          path: `statusHistory[${i}].period`,
          severity: 'error',
        });
      }
    }
  }

  /** ClassHistory: class (1..1), period (1..1). */
  private validateClassHistory(r: Record<string, unknown>, errors: ConversionError[]): void {
    const history = r['classHistory'] as Record<string, unknown>[] | undefined;
    if (!history || !Array.isArray(history)) return;

    for (let i = 0; i < history.length; i++) {
      if (!history[i]['class']) {
        errors.push({
          code: ERROR_CODES.ENC_CLASSHISTORY_MISSING_CLASS,
          message: `classHistory[${i}].class is required`,
          path: `classHistory[${i}].class`,
          severity: 'error',
        });
      }
      if (!history[i]['period']) {
        errors.push({
          code: ERROR_CODES.ENC_CLASSHISTORY_MISSING_PERIOD,
          message: `classHistory[${i}].period is required`,
          path: `classHistory[${i}].period`,
          severity: 'error',
        });
      }
    }
  }

  /** Diagnosis: condition (1..1). */
  private validateDiagnoses(r: Record<string, unknown>, errors: ConversionError[]): void {
    const diagnoses = r['diagnosis'] as Record<string, unknown>[] | undefined;
    if (!diagnoses || !Array.isArray(diagnoses)) return;

    for (let i = 0; i < diagnoses.length; i++) {
      if (!diagnoses[i]['condition']) {
        errors.push({
          code: ERROR_CODES.ENC_DIAGNOSIS_MISSING_CONDITION,
          message: `diagnosis[${i}].condition is required`,
          path: `diagnosis[${i}].condition`,
          severity: 'error',
        });
      }
    }
  }

  /** Location: location (1..1), status value-set. */
  private validateLocations(r: Record<string, unknown>, errors: ConversionError[]): void {
    const locations = r['location'] as Record<string, unknown>[] | undefined;
    if (!locations || !Array.isArray(locations)) return;

    for (let i = 0; i < locations.length; i++) {
      if (!locations[i]['location']) {
        errors.push({
          code: ERROR_CODES.ENC_LOCATION_MISSING_LOCATION,
          message: `location[${i}].location is required`,
          path: `location[${i}].location`,
          severity: 'error',
        });
      }

      const status = locations[i]['status'] as string | undefined;
      if (status && !VALID_LOCATION_STATUSES.includes(status as (typeof VALID_LOCATION_STATUSES)[number])) {
        errors.push({
          code: ERROR_CODES.ENC_INVALID_LOCATION_STATUS,
          message: `location[${i}].status "${status}" is invalid. Must be one of: ${VALID_LOCATION_STATUSES.join(', ')}`,
          path: `location[${i}].status`,
          severity: 'error',
        });
      }
    }
  }
}
